import React from "react";
import * as RadixSwitch from "@radix-ui/react-switch";
import { cn } from "../../lib/cn";

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel?: string;
  disabled?: boolean;
  /** "accent" (default) = cyan-gradient checked track; "mono" = neutral track/thumb
   *  (both states share the dim track; thumb goes off→on grey). Uses --track-dim /
   *  --thumb-off / --thumb-on tokens, so it stays raw-hex-free. */
  tone?: "accent" | "mono";
  className?: string;
}

/** claudesign 36×20 mini inline toggle (Radix Switch). */
export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  ariaLabel,
  disabled,
  tone = "accent",
  className,
}) => (
  <RadixSwitch.Root
    checked={checked}
    onCheckedChange={onChange}
    disabled={disabled}
    aria-label={ariaLabel}
    className={cn(
      "relative inline-flex items-center w-[36px] h-[20px] rounded-full shrink-0",
      tone === "mono" ? "bg-[var(--track-dim)]" : "bg-[#2b2f36]",
      "transition-colors duration-150 cursor-pointer",
      tone !== "mono" && "data-[state=checked]:bg-accent-grad data-[state=checked]:shadow-glow",
      "disabled:cursor-default disabled:opacity-55",
      className,
    )}
  >
    <RadixSwitch.Thumb
      className={cn(
        "block w-[16px] h-[16px] rounded-full",
        tone === "mono" ? "bg-[var(--thumb-off)]" : "bg-[#6b727a]",
        "translate-x-[2px] transition-transform duration-150 will-change-transform",
        tone === "mono"
          ? "data-[state=checked]:translate-x-[18px] data-[state=checked]:bg-[var(--thumb-on)]"
          : "data-[state=checked]:translate-x-[18px] data-[state=checked]:bg-white",
      )}
    />
  </RadixSwitch.Root>
);

export default Switch;
